import { ref, type Ref } from 'vue'
import { useEventListener, useResizeObserver } from '@vueuse/core'

/** A fractional `scrollWidth` can leave a scroller a sub-pixel short of its end. */
const EDGE_TOLERANCE = 1

/**
 * Which sides of a horizontal scroller still hide content.
 *
 * Re-measured on scroll and whenever the scroller or its content changes size —
 * rows arriving widen the table without the scroller itself resizing, so the
 * first child is observed too.
 */
export function useHorizontalOverflow(scroller: Readonly<Ref<HTMLElement | null>>) {
  const canScrollLeft = ref(false)
  const canScrollRight = ref(false)

  function update() {
    const el = scroller.value
    if (!el) {
      canScrollLeft.value = false
      canScrollRight.value = false
      return
    }
    const { scrollLeft, scrollWidth, clientWidth } = el
    canScrollLeft.value = scrollLeft > EDGE_TOLERANCE
    canScrollRight.value = scrollLeft + clientWidth < scrollWidth - EDGE_TOLERANCE
  }

  useEventListener(scroller, 'scroll', update, { passive: true })
  useResizeObserver(
    () => [scroller.value, scroller.value?.firstElementChild as HTMLElement | null | undefined],
    update,
  )

  return { canScrollLeft, canScrollRight }
}

/**
 * Scrolls `scroller` just far enough to show `child`, and nothing else: unlike
 * `scrollIntoView`, it leaves every scrollable ancestor — the page included —
 * where it was. `child`'s `offsetLeft` must be measured from `scroller`, so the
 * scroller has to be positioned.
 */
export function revealInScroller(scroller: HTMLElement, child: HTMLElement) {
  const childRight = child.offsetLeft + child.offsetWidth
  if (child.offsetLeft < scroller.scrollLeft) {
    scroller.scrollLeft = child.offsetLeft
  } else if (childRight > scroller.scrollLeft + scroller.clientWidth) {
    scroller.scrollLeft = childRight - scroller.clientWidth
  }
}
